import { useState } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material'
import { Visibility, VisibilityOff, CheckCircle } from '@mui/icons-material'
import { authApi } from '@api/endpoints/auth'
import { getSaveFriendlyError } from '@utils/errorHandling'

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  // Get data from navigation state or URL params
  const stateEmail = location.state?.email
  const statePhoneNumber = location.state?.phoneNumber
  const stateToken = location.state?.token
  const urlToken = searchParams.get('token')
  const urlEmail = searchParams.get('email')

  const [identifier, setIdentifier] = useState(stateEmail || statePhoneNumber || urlEmail || '')
  const [token, setToken] = useState(stateToken || urlToken || '')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!identifier.trim()) {
      setError('Email or phone number is required')
      return
    }

    if (!token.trim()) {
      setError('Reset token is required')
      return
    }

    if (!newPassword) {
      setError('Please enter a new password')
      return
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setIsLoading(true)

    try {
      const response = await authApi.resetPassword({
        identifier,
        token,
        newPassword,
      })

      setSuccess(true)

      // Show success message with session info
      if (response.sessionsTerminated) {
        alert(
          `Password reset successful! ${response.sessionsTerminated} active session(s) have been terminated for security.`
        )
      }

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login', { state: { message: 'Password reset successful! Please log in.' } })
      }, 2000)
    } catch (err: any) {
      const friendlyError = getSaveFriendlyError(err)
      setError(friendlyError.message || 'Failed to reset password. Please try again or request a new reset link.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          width: '100%',
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center' }}>
          Reset Password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          Enter your new password
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 3 }}
            icon={<CheckCircle />}
          >
            Password reset successful! Redirecting to login...
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {/* Identifier (Email or Phone) */}
          <TextField
            fullWidth
            label="Email or Phone Number"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            disabled={isLoading || success || !!(stateEmail || statePhoneNumber || urlEmail)}
            sx={{ mb: 2 }}
            placeholder="Enter your email or phone number"
          />

          {/* Token (for email flow) */}
          {!stateToken && (
            <TextField
              fullWidth
              label="Reset Token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              disabled={isLoading || success || !!urlToken}
              sx={{ mb: 2 }}
              placeholder="Enter the token from your email"
              helperText="Check your email for the reset token"
            />
          )}

          {/* New Password */}
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={isLoading || success}
            sx={{ mb: 2 }}
            placeholder="Enter new password"
            helperText="Minimum 8 characters"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Confirm Password */}
          <TextField
            fullWidth
            type={showConfirmPassword ? 'text' : 'password'}
            label="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={isLoading || success}
            sx={{ mb: 3 }}
            placeholder="Confirm new password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Submit Button */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading || success}
            sx={{ mb: 2 }}
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Resetting Password...
              </>
            ) : success ? (
              'Password Reset!'
            ) : (
              'Reset Password'
            )}
          </Button>

          {/* Back to Login */}
          {!success && (
            <Button
              fullWidth
              variant="text"
              onClick={() => navigate('/login')}
              disabled={isLoading}
            >
              Back to Login
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  )
}
